<template>
  <div class="plugin-panel" v-loading="loading">
    <el-card class="workspace-card toolbar-card" shadow="hover">
      <div class="page-toolbar">
        <div>
          <div class="toolbar-title">MazdaServer.dll 安全热更新</div>
          <div class="toolbar-desc">
            不覆盖已加载 DLL。上传先在{{ targetLabel }}生成不可变版本、执行 ABI/自检并进入候选灰度，确认后再正式提升。
          </div>
        </div>
        <div class="toolbar-actions">
          <div class="local-only-switch" title="打开后：上传、提升、撤销、回滚、重载只处理本端服务器，不同步另一台服务器">
            <el-switch v-model="localOnly" active-text="不同步对端" />
          </div>
          <el-button :loading="loading" :disabled="operationBusy" @click="loadInfo">刷新</el-button>
          <el-button
            :loading="reloading"
            :disabled="hasCandidate || operationBusy || !coordinatorAllowed"
            @click="reloadDll"
          >
            重新加载活动版本
          </el-button>
        </div>
      </div>
    </el-card>

    <el-alert
      v-if="localOnly"
      title="已开启“不同步对端”"
      description="上传、提升、撤销、回滚和重新加载都只处理本端服务器，不会再同步另一台服务器。调试完成后请记得关闭此开关。"
      type="warning"
      show-icon
      :closable="false"
    />

    <el-alert
      v-if="!coordinatorAllowed"
      title="DLL 双服务器变更统一由 CN 服务器协调"
      description="当前可查看 US 状态；上传、提升、撤销、重载和回滚请切换到 CN Server，CN 会同步操作 US。"
      type="info"
      show-icon
      :closable="false"
    />

    <el-alert
      v-if="operationNotice"
      :title="operationNotice.title"
      :description="operationNotice.message"
      :type="operationNotice.type"
      show-icon
      @close="clearOperationNotice"
    />

    <div class="summary-grid">
      <div class="summary-item">
        <span>生命周期</span>
        <strong>{{ lifecycleText }}</strong>
      </div>
      <div class="summary-item">
        <span>ABI</span>
        <strong>{{ info.abiVersion || '-' }}</strong>
      </div>
      <div class="summary-item">
        <span>灰度比例</span>
        <strong>{{ info.canaryPercent || 0 }}%</strong>
      </div>
      <div class="summary-item">
        <span>保留 Zombie</span>
        <strong>{{ info.zombieCount || 0 }}</strong>
      </div>
      <div class="summary-item wide">
        <span>活动制品</span>
        <strong>{{ info.activeArtifact || '-' }}</strong>
        <small v-if="info.activeSha256">SHA-256：{{ info.activeSha256 }}</small>
      </div>
      <div class="summary-item wide">
        <span>活动版本</span>
        <strong>{{ info.version || '-' }}</strong>
        <small>{{ info.loadedPath || '-' }}</small>
      </div>
    </div>

    <el-alert
      v-if="info.lastError"
      :title="info.lastError"
      type="warning"
      show-icon
      :closable="false"
    />

    <el-card v-if="hasCandidate" class="workspace-card candidate-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>候选灰度</span>
          <el-tag type="warning">尚未正式接管全部业务</el-tag>
        </div>
      </template>

      <div class="candidate-info">
        <div><span>制品</span><strong>{{ info.candidateArtifact }}</strong></div>
        <div><span>事务</span><strong>{{ info.candidateTransaction }}</strong></div>
        <div><span>SHA-256</span><strong>{{ info.candidateSha256 }}</strong></div>
      </div>
      <div class="metric-grid">
        <div><span>灰度调用</span><strong>{{ info.candidateCalls }}</strong></div>
        <div><span>失败</span><strong>{{ info.candidateFailures }}</strong></div>
        <div><span>超时</span><strong>{{ info.candidateTimeouts }}</strong></div>
      </div>
      <el-alert
        title="候选发生 2 次超时，或在至少 5 次调用后失败率达到阈值时，后端会自动撤销候选并继续使用旧活动版本。"
        type="info"
        :closable="false"
        show-icon
      />
      <div class="candidate-actions">
        <el-button
          type="success"
          :loading="promoting"
          :disabled="operationBusy || !coordinatorAllowed"
          @click="promoteDll"
        >
          正式提升到{{ targetLabel }}
        </el-button>
        <el-button
          type="danger"
          plain
          :loading="aborting"
          :disabled="operationBusy || !coordinatorAllowed"
          @click="abortCandidate"
        >
          撤销{{ targetLabel }}候选
        </el-button>
      </div>
    </el-card>

    <el-card class="workspace-card" shadow="hover">
      <template #header>
        <div class="card-header"><span>上传候选 DLL</span></div>
      </template>
      <div class="tip-line">1. 只允许 Windows x64 DLL，最大 180 MB；服务端校验 MZ/PE、DLL 标记、架构、当前 ABI v4 和完整导出。</div>
      <div class="tip-line">2. 文件保存为 C:\mazda\plugin\versions\MazdaServer.&lt;sha256&gt;.dll，不覆盖当前加载文件。</div>
      <div class="tip-line">3. 上传成功仅代表{{ targetLabel }}已 stage + self-test + candidate；必须点击“正式提升”才会切换活动清单。</div>
      <div class="upload-row">
        <el-upload
          v-model:file-list="uploadFileList"
          :auto-upload="false"
          :show-file-list="true"
          :limit="1"
          accept=".dll"
          :disabled="hasCandidate || operationBusy || !coordinatorAllowed"
          :on-change="(file) => chooseFile(file.raw)"
          :on-remove="() => chooseFile(null)"
        >
          <el-button :disabled="hasCandidate || operationBusy || !coordinatorAllowed">选择 DLL</el-button>
        </el-upload>
        <el-button
          type="primary"
          :disabled="hasCandidate || operationBusy || !coordinatorAllowed"
          :loading="uploading"
          @click="uploadDll"
        >
          暂存并启动{{ targetLabel }}灰度
        </el-button>
      </div>
    </el-card>

    <el-card class="workspace-card" shadow="hover">
      <template #header>
        <div class="card-header"><span>不可变版本 / {{ targetLabel }}回滚</span></div>
      </template>
      <el-table :data="historyRows" border stripe empty-text="暂无版本制品">
        <el-table-column prop="name" label="制品文件" min-width="360" />
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-tag v-if="row.active" type="success">活动</el-tag>
            <el-tag v-else-if="row.candidate" type="warning">候选</el-tag>
            <span v-else>历史</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150">
          <template #default="{ row }">
            <el-button
              type="warning"
              link
              :disabled="row.active || hasCandidate || operationBusy || !coordinatorAllowed"
              :loading="rollingBack"
              @click="rollbackDll(row.name)"
            >
              {{ targetLabel }}回滚
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useMazdaPlugin } from '@/composables/mazda/useMazdaPlugin';

const props = defineProps({
  serverName: { type: String, default: '' },
  mazdaPost: { type: Function, required: true },
});

const {
  loading,
  uploading,
  promoting,
  aborting,
  reloading,
  rollingBack,
  operationBusy,
  coordinatorAllowed,
  operationNotice,
  selectedFile,
  info,
  historyRows,
  hasCandidate,
  localOnly,
  loadInfo,
  chooseFile,
  uploadDll,
  promoteDll,
  abortCandidate,
  reloadDll,
  rollbackDll,
  clearOperationNotice,
} = useMazdaPlugin(props.mazdaPost, () => props.serverName);

const uploadFileList = ref([]);
const targetLabel = computed(() => localOnly.value
  ? '本机（不同步对端）'
  : (props.serverName === 'Local Server' ? '本机' : 'CN/US 两端'));

const lifecycleText = computed(() => {
  if (info.value.lifecycleState === 'canary') return '候选灰度中';
  if (info.value.lifecycleState === 'active') return '活动版本';
  return '未加载';
});

watch(() => props.serverName, loadInfo);
watch(selectedFile, (file) => {
  if (!file) uploadFileList.value = [];
});
</script>

<style scoped>
.plugin-panel { display: flex; flex-direction: column; gap: 14px; }
.page-toolbar { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; }
.toolbar-title { font-size: 17px; font-weight: 700; }
.toolbar-desc { margin-top: 5px; color: #667085; font-size: 13px; line-height: 1.6; }
.toolbar-actions, .candidate-actions, .upload-row { display: flex; gap: 12px; flex-wrap: wrap; }
.local-only-switch { display: flex; align-items: center; margin-right: 4px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(130px, 1fr)); gap: 10px; }
.summary-item { padding: 12px 14px; border-radius: 12px; background: #f8fafc; border: 1px solid #eef2f6; }
.summary-item.wide { grid-column: span 2; }
.summary-item span, .candidate-info span, .metric-grid span { display: block; color: #667085; font-size: 12px; }
.summary-item strong, .candidate-info strong, .metric-grid strong { display: block; margin-top: 5px; word-break: break-all; }
.summary-item small { display: block; margin-top: 5px; color: #98a2b3; word-break: break-all; }
.card-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; font-weight: 700; }
.candidate-card { border-color: #f5d49a; }
.candidate-info { display: grid; gap: 10px; padding: 12px; border-radius: 12px; background: #fffbeb; }
.metric-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 12px 0; }
.metric-grid > div { padding: 12px; border-radius: 10px; background: #f8fafc; text-align: center; }
.candidate-actions { margin-top: 14px; }
.tip-line { color: #667085; font-size: 13px; line-height: 1.8; }
.upload-row { align-items: flex-start; margin-top: 14px; }
@media (max-width: 900px) {
  .summary-grid { grid-template-columns: repeat(2, minmax(130px, 1fr)); }
  .summary-item.wide { grid-column: 1 / -1; }
  .page-toolbar { flex-direction: column; }
}
</style>

<!-- MAZDA DLL 热更新确认弹窗样式：ElMessageBox 渲染在 body 下，
     scoped 样式无法作用，故在本 MAZDA 面板组件内使用非 scoped 全局块 -->
<style>
.dll-confirm-body {
  line-height: 1.7;
  font-size: 14px;
  color: #303133;
  word-break: break-word;
}
.dll-confirm-body .dll-confirm-text {
  margin: 0 0 10px;
}
.dll-confirm-body .dll-confirm-text:last-child {
  margin-bottom: 0;
}
.dll-confirm-body .dll-confirm-sub {
  margin-top: 10px;
  color: #909399;
  font-size: 13px;
}
.dll-confirm-body .dll-confirm-warn {
  color: #b45209;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 6px 10px;
}
.dll-confirm-artifact {
  position: relative;
  margin: 4px 0 10px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
}
.dll-confirm-artifact-text {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12.5px;
  color: #475569;
  word-break: break-all;
  overflow-wrap: anywhere;
  white-space: normal;
  user-select: text;
}
.dll-confirm-copy {
  margin-top: 4px;
}
</style>
